/**
 * Local type definitions for Studio UI.
 * Extends and supplements @tatvex/types for UI-specific needs.
 */

import type { ConfigType, ConfigStatus, Environment, Capability, TenantId } from "@tatvex/types";

// Re-export types from @tatvex/types for convenience
export type { ConfigType, ConfigStatus, Environment, Capability, TenantId };

// ============================================================================
// File Tree Types
// ============================================================================

/** File tree node representing a config file or folder */
export interface FileTreeNode {
  readonly id: string;
  readonly name: string;
  readonly type: "folder" | "file";
  readonly path: string;
  readonly children?: readonly FileTreeNode[];
  readonly configType?: ConfigType;
  readonly status?: ConfigStatus;
  readonly isLocked?: boolean;
  readonly lockedBy?: string;
  readonly hasChanges?: boolean;
}

/** File tree selection state */
export interface FileTreeSelection {
  readonly selectedPath: string | undefined;
  readonly expandedPaths: ReadonlySet<string>;
}

// ============================================================================
// Editor Types
// ============================================================================

/** Editor mode */
export type EditorMode = "edit" | "diff" | "readonly";

/** Editor content state */
export interface EditorContent {
  readonly path: string;
  readonly content: string;
  readonly originalContent: string;
  readonly isDirty: boolean;
  readonly language: "json" | "jsonc";
}

/** Editor save status */
export type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Editor toolbar action */
export type EditorAction = "save" | "publish" | "diff" | "history" | "format" | "validate";

// ============================================================================
// Validation Types
// ============================================================================

/** Validation issue severity */
export type ValidationSeverity = "error" | "warning" | "info";

/** Validation issue with location */
export interface ValidationIssue {
  readonly id: string;
  readonly severity: ValidationSeverity;
  readonly message: string;
  readonly path: string;
  readonly line?: number;
  readonly column?: number;
  readonly endLine?: number;
  readonly endColumn?: number;
}

/** Validation result */
export interface ValidationResult {
  readonly isValid: boolean;
  readonly issues: readonly ValidationIssue[];
  readonly validatedAt: string;
}

// ============================================================================
// Auth Types
// ============================================================================

/** User info for display */
export interface UserInfo {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly avatarUrl?: string;
}

/** Auth state */
export interface AuthState {
  readonly isAuthenticated: boolean;
  readonly user: UserInfo | undefined;
  readonly tenantId: TenantId | undefined;
  readonly tenantSlug: string | undefined;
  readonly environment: Environment;
  readonly capabilities: readonly Capability[];
  readonly token: string | undefined;
  readonly tokenExpiresAt: number | undefined;
}

// ============================================================================
// Config Types
// ============================================================================

/** Config metadata for display */
export interface ConfigMetadata {
  readonly path: string;
  readonly configType: ConfigType;
  readonly verticalKey: string;
  readonly version: number;
  readonly status: ConfigStatus;
  readonly checksum: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt?: string;
  readonly updatedBy?: string;
}

/** Config version history entry */
export interface ConfigVersionEntry {
  readonly version: number;
  readonly status: ConfigStatus;
  readonly checksum: string;
  readonly changeSummary?: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly promotedAt?: string;
  readonly promotedBy?: string;
}

// ============================================================================
// API Types
// ============================================================================

/** API error response */
export interface ApiError {
  readonly code: string;
  readonly message: string;
  readonly details?: Record<string, unknown>;
  readonly timestamp: string;
}

/** API response wrapper */
export type ApiResponse<T> =
  | { readonly success: true; readonly data: T }
  | { readonly success: false; readonly error: ApiError };

/** Pagination params */
export interface PaginationParams {
  readonly page?: number;
  readonly pageSize?: number;
  readonly cursor?: string;
}

/** Paginated response */
export interface PaginatedResponse<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly hasMore: boolean;
  readonly nextCursor?: string;
}

// ============================================================================
// UI State Types
// ============================================================================

/** Panel visibility state */
export interface PanelState {
  readonly sidebar: boolean;
  readonly validation: boolean;
  readonly terminal: boolean;
}

/** Toast notification */
export interface Toast {
  readonly id: string;
  readonly type: "success" | "error" | "warning" | "info";
  readonly title: string;
  readonly message?: string;
  readonly duration?: number;
}

/** Modal state */
export interface ModalState {
  readonly isOpen: boolean;
  readonly type: string | undefined;
  readonly data: unknown;
}

// ============================================================================
// Constants
// ============================================================================

/** Config type display names */
export const CONFIG_TYPE_LABELS: Readonly<Record<ConfigType, string>> = {
  schema: "Schema",
  eligibility: "Eligibility",
  ranking: "Ranking",
  journey: "Journey",
  branding: "Branding",
} as const;

/** Environment display names */
export const ENVIRONMENT_LABELS: Readonly<Record<Environment, string>> = {
  dev: "Development",
  staging: "Staging",
  prod: "Production",
} as const;

/** Config status display info */
export const CONFIG_STATUS_INFO: Readonly<Record<ConfigStatus, { label: string; color: string }>> =
  {
    draft: { label: "Draft", color: "warning" },
    live: { label: "Live", color: "success" },
    archived: { label: "Archived", color: "muted" },
  } as const;

// ============================================================================
// Re-export Health Types
// ============================================================================

export type {
  TimeRange,
  WorkerName,
  WorkerHealth,
  D1Metrics,
  D1TimeSeriesPoint,
  KVMetrics,
  KVTimeSeriesPoint,
  R2Metrics,
  R2TimeSeriesPoint,
  WorkerPerformanceMetrics,
  WorkerPerformanceTimeSeriesPoint,
  SystemHealthData,
  HealthThresholds,
} from "./health";

export { TIME_RANGE_OPTIONS, TIME_RANGE_SECONDS, DEFAULT_THRESHOLDS } from "./health";
